import React, { useState, useEffect, useRef } from "react";
import { Canvas } from '@react-three/fiber';
import { Suspense } from 'react';
import Avatar from '../Avatar';
import TypeWriter from '../TypeWriter';
import GlitchText from '../GlitchText';
import TerminalTyping from '../TerminalTyping';
import { personalInfo } from '../../utils/data';
import useSound from '../../hooks/useSound';
import styles from './Hero.module.css';

interface HeroProps {
  setActiveSection: (section: 'hero' | 'about' | 'skills' | 'projects' | 'achievements' | 'responsibility' | 'contact') => void;
}

const Hero: React.FC<HeroProps> = ({ setActiveSection }) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const { playClick, playHover } = useSound();
  const [showTerminal, setShowTerminal] = useState(false);
  const [matrixReady, setMatrixReady] = useState(false);
  const [pulseEffect, setPulseEffect] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    // Check if on mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Show terminal after a short delay
    const timer = setTimeout(() => {
      setShowTerminal(true);
    }, 1500);
    
    // Set matrix ready state after delay
    const matrixTimer = setTimeout(() => {
      setMatrixReady(true);
    }, 3000);
    
    // Start button pulse effect
    const pulseTimer = setInterval(() => {
      setPulseEffect(prev => !prev);
    }, 2000);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(matrixTimer);
      clearInterval(pulseTimer);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  const handleEnterMatrix = () => {
    playClick();
    
    // Add cyber entrance effect
    document.body.classList.add('matrix-entrance');
    
    // First scroll to About section
    setActiveSection('about');
    
    // Remove the effect after animation completes
    setTimeout(() => {
      document.body.classList.remove('matrix-entrance');
    }, 2000);
  };
  
  // Create typed element variables for Three.js elements
  const AmbientLightElement = 'ambientLight' as any;
  const DirectionalLightElement = 'directionalLight' as any;
  
  return (
    <div className={styles.heroSection} ref={heroRef}>
      <div className={styles.hackerOverlay}></div>
      
      <div className={styles.heroContent}>
        <div className={styles.heroTextContainer}>
          <GlitchText 
            text={personalInfo.name}
            fontSize="54px"
            className={styles.heroName}
            intensity="medium"
            highlight={true}
            scanline={true}
          />
          
          <div className={styles.heroTitle}>
            <TypeWriter 
              strings={[
                personalInfo.title,
                "Full Stack Developer",
                "AI/ML Enthusiast",
                "Problem Solver"
              ]}
              speed={50}
              loop={true}
              className={styles.typewriterText}
              cursorClassName={styles.typewriterCursor}
            />
          </div>
          
          {showTerminal && (
            <div className={styles.terminalContainer}>
              <div className={styles.terminalHeader}>
                <div className={styles.terminalButton}></div>
                <div className={styles.terminalButton}></div>
                <div className={styles.terminalButton}></div>
                <span className={styles.terminalTitle}>terminal@matrix</span>
              </div>
              <TerminalTyping 
                text={[
                  "Accessing neural networks...",
                  "Connecting to the matrix...",
                  "Ready to explore my digital realm?",
                  "System ready. Welcome to my portfolio."
                ]}
                typingSpeed={30}
                delayAfterType={1500}
                loop={false}
                className={styles.terminalContent}
              />
            </div>
          )}
          
          <div className={styles.statusIndicator}>
            <div className={`${styles.statusDot} ${matrixReady ? styles.statusReady : styles.statusInitializing}`}></div>
            <span className={styles.statusText}>
              {matrixReady ? 'MATRIX READY' : 'INITIALIZING...'}
            </span>
          </div>
          
          <div className={styles.heroButtons}>
            <button 
              className={`${styles.heroButton} ${pulseEffect ? styles.pulse : ''}`}
              onClick={handleEnterMatrix}
              onMouseEnter={playHover}
              disabled={!matrixReady}
            >
              <span className={styles.buttonGlow}></span>
              <span className={styles.buttonText}>ENTER THE MATRIX</span>
              <span className={styles.buttonBorder}></span>
            </button>
            
            <a 
              href="/resume_aayush_kumar.pdf" 
              download 
              className={`${styles.heroButton} ${styles.secondary} ${styles.downloadButton}`}
              onClick={playClick}
              onMouseEnter={playHover}
            >
              <span className={styles.buttonGlow}></span>
              <span className={styles.buttonText}>
                <svg className={styles.downloadIcon} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                DOWNLOAD CV
              </span>
              <span className={styles.buttonBorder}></span>
            </a>
          </div>
          
          <div className={styles.socialLinks}>
            {/* Add social links here if available in your data */}
          </div>
        </div>
        
        <div className={styles.heroAvatarContainer}>
          {isMobile ? (
            // Mobile version - Static Image with circular frame
            <div className={styles.mobileAvatarFrame}>
              <div className={styles.scanEffect}></div>
              <div className={styles.mobileImageContainer}>
                <img 
                  src="/profile-photo.jpg" 
                  alt={personalInfo.name} 
                  className={styles.mobileProfileImage} 
                />
              </div>
            </div>
          ) : (
            // Desktop version - 3D Canvas
            <div className={styles.avatarFrame}>
              <div className={styles.scanEffect}></div>
              <Canvas
                shadows
                camera={{ position: [0, 0, 5], fov: 50 }}
              >
                <AmbientLightElement intensity={0.5} />
                <DirectionalLightElement
                  position={[10, 10, 5]}
                  intensity={0.8}
                  castShadow
                />
                <Suspense fallback={null}>
                  <Avatar />
                </Suspense>
              </Canvas>
            </div>
          )}
        </div>
      </div>
      
      <div 
        className={`${styles.scrollIndicator} ${matrixReady ? styles.visible : ''}`}
        onClick={() => setActiveSection('about')}
      >
        <div className={styles.scrollText}>INITIALIZE SEQUENCE</div>
        <div className={styles.scrollArrow}></div>
      </div>
      
      <div className={styles.gridOverlay}></div>
    </div>
  );
};

export default Hero;